import { FIRST_PAGE } from 'common/data/constants';
import { Loader, PaginationLm } from 'components/Common';
import { Checkbox } from 'components/Common/Checkbox';
import { SearchInput } from 'components/SearchInput/SerchInput';
import { StyledTable } from 'components/Table/StyledTable';
import { useFormikContext } from 'formik';
import { getFilteredServices, getTags } from 'helpers/api-requests/admin';
import { currentTableData } from 'helpers/currentTableDate';
import { isUserOperator, userCanViewPrice } from 'helpers/jwt_helper';
import { showToastError } from 'helpers/utils/toast';
import uniq from 'lodash/uniq';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Alert, FormGroup } from 'reactstrap';
import { ApplicantServicesRow } from './ApplicantServicesRow';

import styled from 'styled-components';

export const CHECKBOX_STATES = {
  checked: 'Checked', // All selected
  indeterminate: 'Indeterminate', // Some selected
  empty: 'Empty', // None selected
};

const ALL_TAG = { name: 'common.all', id: -1 };

// Styled components for tags
const TagContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: wrap;
  padding: 8px 0;
  border-bottom: 2px solid #e9ecef;
`;

const TagButton = styled.button`
  padding: 8px 20px;
  border: none;
  background: transparent;
  color: ${({ active }) => (active ? '#0d6efd' : '#6c757d')};
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;

  ${({ active }) =>
    active &&
    `
    border-bottom-color: #0d6efd;
  `}

  &:hover {
    color: #0d6efd;
    background: rgba(13, 110, 253, 0.05);
  }
`;

const TagBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  background: ${({ active }) => (active ? '#0d6efd' : '#e9ecef')};
  color: ${({ active }) => (active ? 'white' : '#495057')};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

export const ApplicantServices = ({ index }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { clients } = useSelector(state => state.clients);
  const formik = useFormikContext();

  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [checked, setChecked] = useState(CHECKBOX_STATES.empty);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState(ALL_TAG.name);

  const clientValues = formik.values.client;

  const orderTypeId = formik.values.orderType;

  const currentClientTariff = useMemo(() => {
    if (clientValues.client && clients.results) {
      return clients.results.find(client => client.id === clientValues.client)
        ?.tariff;
    }
  }, [clientValues.client, clients]);

  const hidePrice = !userCanViewPrice();

  const isDisabledFields = !formik.values.orderDetail?.isEditable;

  useEffect(() => {
    if (!currentClientTariff?.id || !isUserOperator) {
      return;
    }

    getTags({ tariff: currentClientTariff.id }).then(response => {
      if (response.success) {
        const preparedTags = Array.from(new Set(response.data.results));

        setTags(preparedTags);
      }
    });
  }, [currentClientTariff?.id]);

  // Helper function to sort services alphabetically (case-insensitive)
  const sortServicesAlphabetically = servicesArray => {
    return [...servicesArray].sort((a, b) => {
      const nameA = a.name?.[language] || '';
      const nameB = b.name?.[language] || '';
      return nameA.localeCompare(nameB, undefined, {
        sensitivity: 'base',
        numeric: true,
        ignorePunctuation: true,
      });
    });
  };

  // fetch services from filter
  useEffect(() => {
    setLoading(true);
    getFilteredServices({
      order_type: orderTypeId,
      client: isUserOperator
        ? {
            type: formik.values.client.clientType,
            id: formik.values.client.client,
          }
        : undefined,
      order_applicant: formik.values.applicants?.[index]?.customerId,
      country: formik.values.client?.country,
      nationality:
        formik.values.applicants?.[index].applicantDetail?.citizenship,
      delivery_method: formik.values.applicants?.[index].deliveryMethod,
      visa_type: formik.values.applicants?.[index].visaType ?? undefined,
      visa_duration:
        formik.values.applicants?.[index].visaDuration ?? undefined,
      visa_urgency: formik.values.applicants?.[index].visaUrgency ?? undefined,
    })
      .then(response => {
        if (response.success) {
          const sortedServices = sortServicesAlphabetically(
            response.data.results,
          );
          setServices(sortedServices);
          return;
        }

        showToastError(response.message);
      })
      .catch(err => showToastError(err))
      .finally(() => setLoading(false));
  }, [
    formik.values.client,
    formik.values.applicants?.[index].applicantDetail?.citizenship,
    formik.values.applicants?.[index].visaType,
    formik.values.applicants?.[index].visaDuration,
    formik.values.applicants?.[index].visaUrgency,
    formik.values.applicants?.[index].deliveryMethod,
  ]);

  useEffect(() => {
    if (
      formik.values.applicants?.[index].services?.length === services.length &&
      services.length > 0
    ) {
      setChecked(CHECKBOX_STATES.checked);
      return;
    }

    if (formik.values.applicants?.[index].services?.length > 0)
      setChecked(CHECKBOX_STATES.indeterminate);

    if (formik.values.applicants?.[index].services?.length === 0)
      setChecked(CHECKBOX_STATES.empty);
  }, [formik.values.applicants?.[index].services]);

  const handleChange = () => {
    formik.values.applicants?.[index].statusApplicant === 'draft' &&
      formik.setFieldValue(`applicants[${index}].firstDateEntry`, null);

    formik.values.applicants?.[index].statusApplicant === 'draft' &&
      formik.setFieldValue(
        `applicants[${index}].documentsToApplicantEstimatedAt`,
        null,
      );
    if (checked === CHECKBOX_STATES.checked) {
      formik.setFieldValue(`applicants.[${index}].services`, []);
      formik.setFieldValue(`applicants[${index}].unCheckedServices`, [
        ...(formik.values.applicants?.[index].unCheckedServices ?? []),
        ...formik.values.applicants?.[index].services.map(
          service => service.id,
        ),
      ]);
    } else if (checked === CHECKBOX_STATES.empty) {
      const servicesToAdd =
        activeTag === ALL_TAG.name
          ? services
          : services.filter(service =>
              service.tags?.some(tag => tag === activeTag),
            );

      formik.setFieldValue(
        `applicants.[${index}].services`,
        sortServicesAlphabetically(servicesToAdd),
      );
    } else if (checked === CHECKBOX_STATES.indeterminate) {
      formik.setFieldValue(`applicants.[${index}].services`, []);
      formik.setFieldValue(`applicants[${index}].unCheckedServices`, [
        ...(formik.values.applicants?.[index].unCheckedServices ?? []),
        ...formik.values.applicants?.[index].services.map(
          service => service.id,
        ),
      ]);
    }
  };

  const defaultChecked = services => {
    const formikServiceId = formik.values.applicants?.[index].services.map(
      product => product.id,
    );
    const unCheckedServices =
      formik.values.applicants?.[index].unCheckedServices;

    const autoSelected = services
      .filter(
        service => service.auto_select && !formikServiceId.includes(service.id),
      )
      .filter(service =>
        unCheckedServices ? !unCheckedServices.includes(service.id) : true,
      );

    const result = [
      ...formik.values.applicants?.[index].services,
      ...autoSelected,
    ];

    formik.setFieldValue(
      `applicants.[${index}].services`,
      sortServicesAlphabetically(result),
    );
  };

  useEffect(() => {
    if (services && !isDisabledFields) {
      defaultChecked(services);
    }
  }, [services]);

  // This filters services based on search and active tag - used for display
  const filteredServices = useMemo(() => {
    setCurrentPage(1);
    let l1 = services.filter(service =>
      service.name[language].toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const l2 = formik.values.applicants?.[index].services.filter(
      c_service =>
        !l1
          .map(key => {
            return key.id;
          })
          .includes(c_service.id),
    );

    const initialServices =
      l2.length > 0
        ? [
            ...l1,
            // l2 is commented since only searched for services should be visible
            // ...l2
          ]
        : l1;

    const filtered =
      activeTag === ALL_TAG.name
        ? initialServices
        : initialServices.filter(service => {
            return service.tags?.some(tag => tag === activeTag);
          });

    return sortServicesAlphabetically(filtered);
  }, [searchQuery, services, activeTag]);

  // FIX: Count services for each tag based on ALL services (not filtered)
  const getTagCount = useCallback(
    tagName => {
      if (tagName === ALL_TAG.name) {
        return services.length; // Total services, not filtered
      }
      return services.filter(service =>
        service.tags?.some(tag => tag === tagName),
      ).length;
    },
    [services],
  );

  // Get sorted services with selected first, then alphabetically
  const getSortedServices = useCallback(
    servicesToSort => {
      return [...servicesToSort].sort((a, b) => {
        const isASelected = formik.values.applicants?.[index].services.some(
          service => service.id === a.id,
        );
        const isBSelected = formik.values.applicants?.[index].services.some(
          service => service.id === b.id,
        );

        if (isASelected && !isBSelected) return -1;
        if (!isASelected && isBSelected) return 1;

        const nameA = a.name?.[language] || '';
        const nameB = b.name?.[language] || '';
        return nameA.localeCompare(nameB, undefined, {
          sensitivity: 'base',
          numeric: true,
          ignorePunctuation: true,
        });
      });
    },
    [formik.values.applicants?.[index].services, language],
  );

  const showPagination = 10 < filteredServices.length;
  const currentServices = currentTableData(
    getSortedServices(filteredServices),
    currentPage,
    10,
  );

  const tagsFromServices = useMemo(() => {
    const servicesTags = services?.reduce(
      (acc, service) => (service.tags ? [...acc, ...service.tags] : acc),
      [],
    );

    return uniq(servicesTags);
  }, [services]);

  const mergedTags = useMemo(() => {
    const filteredTags = tags.filter(
      tag => !!tagsFromServices.includes(tag.name),
    );

    return [ALL_TAG.name, ...filteredTags.map(tag => tag.name)];
  }, [tags, tagsFromServices]);

  const handleChangeTag = tag => () => {
    setCurrentPage(FIRST_PAGE);
    setActiveTag(tag);
  };

  return (
    <div className="mt-4">
      <FormGroup row>
        <SearchInput
          value={searchQuery}
          handleChange={event => setSearchQuery(event.target.value)}
          className="w-50"
        />
      </FormGroup>

      {isUserOperator && (
        <TagContainer>
          {mergedTags.map(tag => (
            <TagButton
              key={tag}
              active={activeTag === tag}
              onClick={handleChangeTag(tag)}
            >
              {t(tag)}
              <TagBadge active={activeTag === tag}>{getTagCount(tag)}</TagBadge>
            </TagButton>
          ))}
        </TagContainer>
      )}

      {loading && (
        <div className="w-100 d-flex align-items-center justify-content-center">
          <Loader isLoading={loading} />
        </div>
      )}

      {!loading && filteredServices.length === 0 && (
        <Alert className="mt-3" color="info">
          {t('Services not found')}
        </Alert>
      )}

      {!loading && filteredServices.length > 0 && (
        <>
          <StyledTable className="mt-3" responsive>
            <thead>
              <tr>
                <th>
                  <Checkbox
                    disabled={
                      (!isUserOperator &&
                        formik.values.applicants?.[index].services?.some(
                          service => service.is_visa_type,
                        )) ||
                      isDisabledFields
                    }
                    checked={checked}
                    onChange={handleChange}
                  />
                </th>

                <th>{t('common.service')}</th>

                <th>{t('common.type')}</th>

                {!hidePrice && (
                  <>
                    <th>{t('common.price')}</th>

                    <th>{t('common.tax')}</th>

                    <th>{t('common.total')}</th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {currentServices.map(service => (
                <ApplicantServicesRow
                  service={service}
                  index={index}
                  key={service.id}
                  isDisabledFields={isDisabledFields}
                />
              ))}
            </tbody>
          </StyledTable>

          {showPagination && (
            <PaginationLm
              count={filteredServices.length}
              perPage={10}
              active={currentPage}
              selectPage={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};
